import {Button} from '@material-ui/core';
import {BeneficiariesRouteConstants} from 'Beneficiaries/Routes';
import {BOSToolsRouteConstants} from 'BOSTools/Routes';
import DocumentationMenu from 'Core/components/Navigation/DocumentationMenu';
import EnrollTrackMenu from 'Core/components/Navigation/EnrollTrackMenu';
import LegacyAccess from 'Core/components/Navigation/LegacyAccess';
import MyAccessMenu from 'Core/components/Navigation/MyAccessMenu';
import MessagingMenu from 'Core/components/Navigation/MessagingMenu';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import React from 'react';
import {connect} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router';
import {Link} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import RequireScope from 'Shared/components/RequireScope';
import {Scope} from 'Shared/constants/Scope';
import RequireAtLeastOneScope from 'Shared/components/RequireAtLeastOneScope';
import {loadUserClaims, selectUserScopes} from 'Users/selectors/userProfileSelectors';
import './NavigationMenu.sass';
import {basename} from 'path';
import {AsyncUserClaims, UserTypeClaims, UserTypeClaimKey} from 'Users/models/Claims';
import {flow} from 'fp-ts/lib/function';
import {AgentClaims} from 'Users/models/AgentClaims';

type NavigationMenuProps = ReturnType<typeof mapStateToProps> & {userClaims: AsyncUserClaims} & RouteComponentProps;

export const NavigationMenu = (props: NavigationMenuProps) => {
  const {location, userClaims} = props;
  const {pathname} = location;
  const pathTokens = pathname.split('/');

  let isEnrollTrack, isMessaging, isBeneficiaries, isBosTools, isAgent;
  isEnrollTrack = isMessaging = isBeneficiaries = isBosTools = isAgent = false;
  const claims = loadUserClaims(userClaims);

  const disableNavigation = pathname === '/';

  if (pathTokens.length > 1) {
    const lineOfBusinessPath = pathTokens[1];

    isEnrollTrack = lineOfBusinessPath === 'enrollment';
    isMessaging = lineOfBusinessPath === 'messaging';
    isBeneficiaries = lineOfBusinessPath === 'beneficiaries';
    isBosTools = lineOfBusinessPath === 'bostools';
    isAgent = lineOfBusinessPath === 'agent' || basename(pathname) === 'ssoAccounts';
  }
  const hasAgencyAssociations = flow(
    (agentClaims: AgentClaims) => agentClaims.agencyAssociations,
    associations => associations.length > 0
  );

  const doesAgentUserHaveAgencies = (agentClaims: AgentClaims) => hasAgencyAssociations(agentClaims);
  const hasAtleastOneAgencyAssociation = claims.userTypes
    .map(
      UserTypeClaims.match({
        [UserTypeClaimKey.AgentSite]: agentClaims => {
          const claims = agentClaims.impersonatedAgentClaim ? agentClaims.impersonatedAgentClaim : agentClaims;
          return doesAgentUserHaveAgencies(claims);
        },
        default: () => false,
      })
    )
    .some(Boolean);

  return (
    <div
      style={{
        padding: disableNavigation ? '0 13.5rem' : '0 3.5rem',
      }}
      className="navigation-menu"
    >
      {!disableNavigation && (
        <>
          {isAgent && (
            <>
              <MyAccessMenu {...props} />
              <DocumentationMenu />
              <RequireScope scopes={props.scopes} scope={Scope.AgentResources}>
                <LegacyAccess />
              </RequireScope>
            </>
          )}
          {isEnrollTrack && <EnrollTrackMenu scopes={props.scopes} />}
          {isEnrollTrack && (
            <RequireAtLeastOneScope scopes={[Scope.EnrollTrackAdministrator, Scope.UploadBrokerSpreadsheet]}>
              <Link to={EnrollmentRouteConstants.UPLOAD_ENROLLMENT_FILE_APPLICATIONS}>
                <Button id="administration-menu-button" className="my-access-menu" variant="text">
                  File Upload
                </Button>
              </Link>
            </RequireAtLeastOneScope>
          )}
          {isEnrollTrack && (
            <RequireAtLeastOneScope scopes={[Scope.EnrollTrackAdministrator, Scope.EmployerProfileUpload]}>
              <Link to={EnrollmentRouteConstants.UPLOAD_ENROLLMENT_EMPLOYER_PROFILES}>
                <Button id="administration-menu-button" className="my-access-menu" variant="text">
                  Bulk Profile Setup
                </Button>
              </Link>
            </RequireAtLeastOneScope>
          )}
          {isEnrollTrack && (
            <RequireAtLeastOneScope scopes={[Scope.EnrollTrackAdministrator, Scope.EmployerProfileUpload]}>
              <Link to={EnrollmentRouteConstants.GENELCO_BATCH_FILE_MANAGEMENT}>
                <Button id="administration-menu-button" className="my-access-menu" variant="text">
                  Genelco Batch File Management
                </Button>
              </Link>
            </RequireAtLeastOneScope>
          )}
          {isBeneficiaries && (
            <RequireScope scopes={props.scopes} scope={Scope.BeneficiariesSearch}>
              <Link to={BeneficiariesRouteConstants.BENEFICIARIES}>
                <Button id="administration-menu-button" className="my-access-menu" variant="text">
                  Beneficiaries
                </Button>
              </Link>
            </RequireScope>
          )}
          {isBosTools && (
            <RequireScope scopes={props.scopes} scope={Scope.BOSTools}>
              <Link to={BOSToolsRouteConstants.BOSTOOLS}>
                <Button id="administration-menu-button" className="my-access-menu" variant="text">
                  BOS Tools
                </Button>
              </Link>
            </RequireScope>
          )}
          {isMessaging && <MessagingMenu scopes={props.scopes} />}

          {isEnrollTrack && hasAtleastOneAgencyAssociation && (
            <RequireAtLeastOneScope scopes={[Scope.EnrollmentFilesSearchByAgentNumbers]}>
              <Link to={EnrollmentRouteConstants.EMPLOYER_ENROLLMENTS}>
                <Button id="administration-menu-button" className="my-access-menu" variant="text">
                  Account Enrollments
                </Button>
              </Link>
            </RequireAtLeastOneScope>
          )}
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: ReducerState) => ({
  scopes: selectUserScopes(state),
});

export default withRouter(connect(mapStateToProps)(NavigationMenu));
